import React, { useRef, useEffect, useState, useCallback } from 'react';
import useCanvasDrag from './useCanvasDrag';
import { loadImage, getCachedImage } from '../shared/imageCache';
import { syncLayerOrder } from './LayerPanel';
import { getRefImage } from '../shared/thumbnailCache';
import renderLookComposite from '../shared/renderLookComposite';

const CANVAS_MAX_DEFAULT = 500; // fallback max preview size in CSS px

const substituteVars = (text, data) => {
  if (!text || !data) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => data[key] ?? match);
};

// Font loading: load fonts from S3 via @font-face and track loaded status
const loadedFonts = new Set();
const loadFont = async (fontFilename, fontS3Path, loadImageFn) => {
  if (!fontFilename || loadedFonts.has(fontFilename)) return;
  loadedFonts.add(fontFilename); // mark as attempted immediately to prevent duplicate loads
  const familyName = fontFilename.replace(/\.[^.]+$/, ''); // strip extension
  try {
    const s3Key = (fontS3Path || 's3://shoobphoto/fonts/') +
      ((fontS3Path || '').endsWith('/') ? '' : '/') + fontFilename;
    // Fetch a presigned URL for the font file
    const { fetchPresignedUrl } = await import('../shared/api');
    const url = await fetchPresignedUrl(s3Key.replace(/^s3:\/\/[^/]+\//, ''));
    const face = new FontFace(familyName, `url(${url})`);
    const loaded = await face.load();
    document.fonts.add(loaded);
  } catch (err) {
    // Allow retry on failure
    loadedFonts.delete(fontFilename);
  }
};

const getFontFamily = (fontFilename) => {
  if (!fontFilename) return 'sans-serif';
  const familyName = fontFilename.replace(/\.[^.]+$/, '');
  return loadedFonts.has(fontFilename) ? `"${familyName}", sans-serif` : 'sans-serif';
};

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 5, 6];

const LookCanvas = ({ formData, allLooks, onChange, onCommit, simulationData, onElementSelect, textSizePreview = 'max' }) => {
  const canvasRef = useRef(null);
  const [refImg, setRefImg] = useState(null);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [containerSize, setContainerSize] = useState(null);

  // Ref photo drag state (local, not persisted)
  const [refDrag, setRefDrag] = useState(null);
  const refDragRef = useRef(null);
  const scrollAreaRef = useRef(null);

  // Measure the scroll area container to size the canvas to fit
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    let rafId;
    const observer = new ResizeObserver(entries => {
      // Use requestAnimationFrame to avoid ResizeObserver loop errors
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const entry = entries[0];
        if (entry) {
          setContainerSize(prev => {
            const w = Math.round(entry.contentRect.width);
            const h = Math.round(entry.contentRect.height);
            if (prev && prev.width === w && prev.height === h) return prev;
            return { width: w, height: h };
          });
        }
      });
    });
    observer.observe(el);
    return () => { observer.disconnect(); cancelAnimationFrame(rafId); };
  }, []);

  // Scroll-wheel zoom (Ctrl/Cmd + scroll) — must use native listener to call preventDefault
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) zoomIn();
        else zoomOut();
      }
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const cw = formData.canvas_width || 2400;
  const ch = formData.canvas_height || 3000;
  const aspect = cw / ch;

  // Size the canvas to fit the available container, falling back to default
  const padding = 40;
  let previewH, previewW;
  if (containerSize) {
    const availH = containerSize.height - padding;
    const availW = containerSize.width - padding;
    const fitByH = availH;
    const fitByW = availW / aspect;
    previewH = Math.round(Math.min(fitByH, fitByW, ch));
    previewW = Math.round(previewH * aspect);
  } else {
    previewH = Math.min(CANVAS_MAX_DEFAULT, ch);
    previewW = Math.round(previewH * aspect);
  }

  // Helper: convert percentage value to preview pixel position
  const pctToX = (pct) => (pct || 0) * previewW;
  const pctToY = (pct) => (pct || 0) * previewH;

  const zoomIn = () => setCanvasZoom(z => {
    const next = ZOOM_STEPS.find(s => s > z);
    return next || z;
  });
  const zoomOut = () => setCanvasZoom(z => {
    const prev = [...ZOOM_STEPS].reverse().find(s => s < z);
    return prev || z;
  });

  const { handlers: dragHandlers, alignmentGuides, hoveredElement, selectedElement, clearSelection } = useCanvasDrag({
    formData, previewW, previewH,
    onChange: onChange || (() => {}),
    onCommit: onCommit || (() => {}),
    zoom: canvasZoom
  });

  // Legacy ref photo positioning (used only when no face placement data exists)
  const refZoom = formData.ref_zoom ?? 1;
  const refRotation = formData.ref_rotation ?? 0;
  const refOffsetX = formData.ref_offset_x ?? 0;
  const refOffsetY = formData.ref_offset_y ?? 0;
  const hasFacePlacement = formData.eyes_y != null && formData.face_height != null && formData.center_x != null;

  // Load collage background reference image (base64)
  const [canvasBgRefImg, setCanvasBgRefImg] = useState(null);

  useEffect(() => {
    if (formData.canvas_bg_ref_data) {
      const img = new Image();
      img.onload = () => setCanvasBgRefImg(img);
      img.src = formData.canvas_bg_ref_data;
    } else {
      setCanvasBgRefImg(null);
    }
  }, [formData.canvas_bg_ref_data]);

  // Load canvas background from S3 path (fallback when no base64 data)
  const [canvasBgImg, setCanvasBgImg] = useState(null);
  const canvasBgKey = (formData.canvas_filepath && formData.canvas_filename)
    ? formData.canvas_filepath + (formData.canvas_filepath.endsWith('/') ? '' : '/') + formData.canvas_filename
    : null;

  useEffect(() => {
    if (!canvasBgKey) { setCanvasBgImg(null); return; }
    const cached = getCachedImage(canvasBgKey);
    if (cached) { setCanvasBgImg(cached); return; }
    loadImage(canvasBgKey)
      .then(img => setCanvasBgImg(img))
      .catch(() => setCanvasBgImg(null));
  }, [canvasBgKey]);

  // Load reference photo — use thumb as fallback when full data is omitted (e.g. summary serialization)
  const refPhotoSrc = formData.ref_photo_data || formData.ref_photo_thumb;
  useEffect(() => {
    if (refPhotoSrc) {
      const img = new Image();
      img.onload = () => setRefImg(img);
      img.src = refPhotoSrc;
    } else {
      setRefImg(null);
    }
  }, [refPhotoSrc]);

  // Font load counter — incremented each time a font finishes loading to trigger redraw
  const [fontLoadTick, setFontLoadTick] = useState(0);

  // Load fonts used by text fields
  useEffect(() => {
    (formData.text_fields || []).forEach(tf => {
      if (tf.font_filename && !loadedFonts.has(tf.font_filename)) {
        loadFont(tf.font_filename, tf.font_s3_path).then(() => {
          setFontLoadTick(t => t + 1);
        });
      }
    });
  }, [formData.text_fields]);

  // Collage photo preview images keyed by look_ref or pose_id
  const [collagePhotoImgs, setCollagePhotoImgs] = useState({});

  useEffect(() => {
    if (formData.look_type !== 'collage') return;
    const photos = formData.collage_photos || [];

    photos.forEach(photo => {
      // Look template source — render full composite (overlay, text, bg, ref photo)
      if (photo.look_ref && !photo.source_type?.startsWith('database')) {
        const key = photo.look_ref;
        if (collagePhotoImgs[key]) return;
        if (!allLooks) return;
        const refLook = allLooks.find(l => l.name === photo.look_ref);
        if (!refLook) return;
        // Render at a reasonable resolution for the collage slot
        const slotW = Math.round((photo.width || 0.3) * (formData.canvas_width || 2400));
        const slotH = Math.round((photo.height || 0.3) * (formData.canvas_height || 3000));
        const renderSize = Math.min(960, Math.max(slotW, slotH));
        renderLookComposite(refLook, renderSize, renderSize, allLooks).then(img => {
          if (img) setCollagePhotoImgs(prev => ({ ...prev, [key]: { img, look: refLook } }));
        });
      }

      // Database pose source
      if (photo.source_type === 'database' && photo.pose_thumbnail_url) {
        const key = `db_${photo.pose_id}`;
        if (collagePhotoImgs[key]) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setCollagePhotoImgs(prev => ({ ...prev, [key]: { img, look: null } }));
        };
        img.src = photo.pose_thumbnail_url;
      }
    });
  }, [formData.collage_photos, formData.look_type, allLooks]);

  // Overlay image from S3
  const [overlayImg, setOverlayImg] = useState(null);
  const overlayKey = (formData.overlay_s3_path && formData.overlay_filename)
    ? formData.overlay_s3_path + (formData.overlay_s3_path.endsWith('/') ? '' : '/') + formData.overlay_filename
    : null;

  useEffect(() => {
    if (!overlayKey) { setOverlayImg(null); return; }
    // Check cache first
    const cached = getCachedImage(overlayKey);
    if (cached) { setOverlayImg(cached); return; }
    // Load async
    loadImage(overlayKey)
      .then(img => setOverlayImg(img))
      .catch(() => setOverlayImg(null));
  }, [overlayKey]);

  // Ref photo drag handlers — wrapped around useCanvasDrag handlers
  const handleMouseDown = useCallback((e) => {
    if (refImg && e.shiftKey) {
      // Shift+click = drag ref photo (always moves ref offset, independent of face oval)
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const startX = (e.clientX - rect.left) / canvasZoom;
      const startY = (e.clientY - rect.top) / canvasZoom;
      const state = {
        startX, startY,
        startOffsetX: refOffsetX, startOffsetY: refOffsetY
      };
      setRefDrag(state);
      refDragRef.current = state;
      return;
    }
    // Track selected element for layer controls
    if (onElementSelect) {
      onElementSelect(hoveredElement);
    }
    dragHandlers.onMouseDown(e);
  }, [refImg, refOffsetX, refOffsetY, dragHandlers, canvasZoom, onElementSelect, hoveredElement]);

  const handleMouseMove = useCallback((e) => {
    if (refDragRef.current) {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const mx = (e.clientX - rect.left) / canvasZoom;
      const my = (e.clientY - rect.top) / canvasZoom;
      const ds = refDragRef.current;
      const dx = (mx - ds.startX) / previewW;
      const dy = (my - ds.startY) / previewH;

      if (onChange) {
        onChange({
          ...formData,
          ref_offset_x: ds.startOffsetX + dx,
          ref_offset_y: ds.startOffsetY + dy
        });
      }
      e.currentTarget.style.cursor = 'move';
      return;
    }
    dragHandlers.onMouseMove(e);
  }, [formData, onChange, previewW, previewH, dragHandlers, canvasZoom]);

  const handleMouseUp = useCallback((e) => {
    if (refDragRef.current) {
      refDragRef.current = null;
      setRefDrag(null);
      if (onCommit) onCommit();
      return;
    }
    dragHandlers.onMouseUp(e);
  }, [onCommit, dragHandlers]);

  const handleMouseLeave = useCallback((e) => {
    if (refDragRef.current) {
      refDragRef.current = null;
      setRefDrag(null);
      if (onCommit) onCommit();
      return;
    }
    dragHandlers.onMouseLeave(e);
  }, [onCommit, dragHandlers]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Render at zoomed resolution for crisp output at all zoom levels
    const renderScale = canvasZoom;
    canvas.width = Math.round(previewW * renderScale);
    canvas.height = Math.round(previewH * renderScale);
    const ctx = canvas.getContext('2d');
    ctx.scale(renderScale, renderScale);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, previewW, previewH);

    // Background image (rendered behind everything)
    if (canvasBgRefImg) {
      ctx.drawImage(canvasBgRefImg, 0, 0, previewW, previewH);
    } else if (canvasBgImg) {
      ctx.drawImage(canvasBgImg, 0, 0, previewW, previewH);
    }

    // Reference photo — positioned independently using ref_offset/zoom/rotation
    if (refImg) {
      ctx.save();
      const imgAspect = refImg.width / refImg.height;
      let dw, dh;
      if (imgAspect > aspect) { dh = previewH; dw = dh * imgAspect; }
      else { dw = previewW; dh = dw / imgAspect; }
      dw *= refZoom;
      dh *= refZoom;

      const cx = previewW / 2 + refOffsetX * previewW;
      const cy = previewH / 2 + refOffsetY * previewH;
      ctx.translate(cx, cy);
      ctx.rotate((refRotation * Math.PI) / 180);
      ctx.globalAlpha = 0.4;
      ctx.drawImage(refImg, -dw / 2, -dh / 2, dw, dh);
      ctx.globalAlpha = 1.0;
      ctx.restore();
    }

    // === Layer-ordered rendering ===
    const layers = syncLayerOrder(formData.layer_order, formData);
    const PHOTO_COLORS = ['#1976d2', '#e91e63', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#795548', '#607d8b'];

    layers.forEach(layer => {
      // --- Overlay ---
      if (layer.type === 'overlay') {
        if (overlayImg) {
          ctx.drawImage(overlayImg, 0, 0, previewW, previewH);
        } else if (formData.overlay_filename) {
          ctx.fillStyle = 'rgba(156, 39, 176, 0.08)';
          ctx.fillRect(0, 0, previewW, previewH);
          ctx.fillStyle = '#9c27b0';
          ctx.font = '10px sans-serif';
          ctx.fillText(`Overlay: ${formData.overlay_filename} (loading...)`, 8, previewH - 8);
        }
      }

      // --- Collage photo ---
      if (layer.type === 'collage_photo' && formData.look_type === 'collage') {
        const i = layer.index;
        const photos = formData.collage_photos || [];
        const photo = photos[i];
        if (!photo || photo.x == null || photo.y == null || photo.width == null || photo.height == null) return;
        const px = pctToX(photo.x);
        const py = pctToY(photo.y);
        const pw = pctToX(photo.width);
        const ph = pctToY(photo.height);
        const color = PHOTO_COLORS[i % PHOTO_COLORS.length];
        const selectedIdx = formData._selectedCollagePhoto;
        const isSelected = selectedIdx === i;
        const isHovered = hoveredElement?.type === 'collage_photo' && hoveredElement?.index === i;

        // Look up image: look template by look_ref, database pose by db_poseId
        const imgKey = photo.source_type === 'database' ? `db_${photo.pose_id}` : photo.look_ref;
        const photoData = imgKey ? collagePhotoImgs[imgKey] : null;
        if (photoData && photoData.img) {
          const look = photoData.look;
          const pImg = photoData.img;
          ctx.save();
          ctx.beginPath();
          ctx.rect(px, py, pw, ph);
          ctx.clip();
          // Cover-fill the rectangle with the image
          const imgAspect = pImg.width / pImg.height;
          const rectAspect = pw / ph;
          let dw, dh;
          if (imgAspect > rectAspect) { dh = ph; dw = dh * imgAspect; }
          else { dw = pw; dh = dw / imgAspect; }
          ctx.drawImage(pImg, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh);
          ctx.restore();
        } else {
          ctx.fillStyle = color + '18';
          ctx.fillRect(px, py, pw, ph);
          ctx.fillStyle = color;
          ctx.font = `${isSelected || isHovered ? 'bold ' : ''}11px sans-serif`;
          ctx.textAlign = 'center';
          const label = photo.source_type === 'database' ? (photo.pose_name || `DB Pose`) : (photo.look_ref || `Photo ${i + 1}`);
          ctx.fillText(label, px + pw / 2, py + ph / 2 + 4);
          ctx.textAlign = 'start';
        }

        ctx.strokeStyle = isSelected ? color : (isHovered ? color : color + '80');
        ctx.lineWidth = isSelected ? 2.5 : (isHovered ? 2 : 1);
        ctx.setLineDash(isSelected ? [] : [5, 3]);
        ctx.strokeRect(px, py, pw, ph);
        ctx.setLineDash([]);

        if (isSelected || isHovered) {
          const handleSize = 5;
          ctx.fillStyle = color;
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 1.5;
          [[px, py], [px + pw, py], [px, py + ph], [px + pw, py + ph]].forEach(([cx, cy]) => {
            ctx.beginPath(); ctx.rect(cx - handleSize, cy - handleSize, handleSize * 2, handleSize * 2); ctx.fill(); ctx.stroke();
          });
          [[px + pw / 2, py], [px + pw / 2, py + ph], [px, py + ph / 2], [px + pw, py + ph / 2]].forEach(([ex, ey]) => {
            ctx.beginPath(); ctx.rect(ex - 3, ey - 3, 6, 6); ctx.fill(); ctx.stroke();
          });
        }
      }

      // --- Face placement (not shown for collages) ---
      if (layer.type === 'face' && formData.look_type !== 'collage' && formData.eyes_y && formData.face_height && formData.center_x) {
      const isHovered = hoveredElement?.type === 'face';
      const isSelected = selectedElement?.type === 'face';
      const hoverAction = hoveredElement?.action;
      const cx = pctToX(formData.center_x);
      const ey = pctToY(formData.eyes_y);
      const fh = Math.abs(pctToY(formData.face_height)) || 1;
      const ovalCY = ey + fh * 0.1;
      const rx = Math.max(1, fh * 0.35);
      const ry = Math.max(1, fh / 2);
      const rot = (formData.rotation || 0) * Math.PI / 180;

      ctx.save();
      ctx.translate(cx, ovalCY);
      ctx.rotate(rot);

      // Face bounding oval
      ctx.strokeStyle = (isSelected || isHovered) ? '#c2185b' : '#e91e63';
      ctx.lineWidth = (isSelected || isHovered) ? 3 : 2;
      ctx.setLineDash([6, 3]);
      ctx.beginPath();
      ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();

      // Crosshair at eyes position (ey is above ovalCY by fh*0.1)
      const eyeLocalY = ey - ovalCY; // negative offset
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(-6, eyeLocalY);
      ctx.lineTo(6, eyeLocalY);
      ctx.moveTo(0, eyeLocalY - 6);
      ctx.lineTo(0, eyeLocalY + 6);
      ctx.stroke();

      // Handles (shown on hover or selection)
      if (isHovered || isSelected) {
        const handleSize = 5;
        ctx.fillStyle = '#e91e63';
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;

        // Top handle
        ctx.beginPath();
        ctx.rect(-handleSize, -ry - handleSize, handleSize * 2, handleSize * 2);
        ctx.fill();
        ctx.stroke();

        // Bottom handle
        ctx.beginPath();
        ctx.rect(-handleSize, ry - handleSize, handleSize * 2, handleSize * 2);
        ctx.fill();
        ctx.stroke();

        // Left handle
        ctx.beginPath();
        ctx.rect(-rx - handleSize, -handleSize, handleSize * 2, handleSize * 2);
        ctx.fill();
        ctx.stroke();

        // Right handle
        ctx.beginPath();
        ctx.rect(rx - handleSize, -handleSize, handleSize * 2, handleSize * 2);
        ctx.fill();
        ctx.stroke();

        // Rotation handle — line + circle above the oval
        const rotHandleY = -ry - 24;
        ctx.strokeStyle = '#e91e63';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(0, -ry);
        ctx.lineTo(0, rotHandleY);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.arc(0, rotHandleY, 5, 0, Math.PI * 2);
        ctx.fillStyle = hoverAction === 'rotate' ? '#c2185b' : '#e91e63';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Rotation arc icon inside the handle
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(0, rotHandleY, 3, -Math.PI * 0.7, Math.PI * 0.3);
        ctx.stroke();
      }

      ctx.restore();
    }

      // --- Text field ---
      if (layer.type === 'text') {
        const i = layer.index;
        const tf = (formData.text_fields || [])[i];
        if (tf && tf.center_x != null && tf.center_y != null) {
          const isHovered = hoveredElement?.type === 'text' && hoveredElement?.index === i;
          const isSelected = selectedElement?.type === 'text' && selectedElement?.index === i;
          const tx = pctToX(tf.center_x);
          const ty = pctToY(tf.center_y);
          const left = pctToX(tf.bound_x_left || 0);
          const right = pctToX(tf.bound_x_right || 1);

          const canvasH = formData.canvas_height || 3000;
          const scaleY = previewH / canvasH;
          // Box outline always reflects font_size_max — the maximum reserved
          // vertical space for this field regardless of which size we're
          // previewing.
          const maxFontPreviewPx = (tf.font_size_max || 160) * scaleY;
          const halfHeight = Math.max(12, maxFontPreviewPx * 0.7);

          // Preview target: font_size_max (default) or font_size_min. Falls back
          // to the other when one is unset.
          const targetPt = (textSizePreview === 'min'
            ? (tf.font_size_min ?? tf.font_size_max ?? 160)
            : (tf.font_size_max ?? tf.font_size_min ?? 160));
          const targetPreviewPx = targetPt * scaleY;

          const displayText = simulationData
            ? substituteVars(tf.content, simulationData)
            : (tf.content || `Field ${i + 1}`);
          const fontFamily = getFontFamily(tf.font_filename);
          const fontVariant = `${tf.italic ? 'italic ' : ''}${tf.bold ? 'bold ' : ''}`;

          // Measure the text at the (uncapped) target font size. If it would
          // overflow the horizontal bounds, shrink to fit so preview text
          // never draws past the box the way it did before.
          const boxWidth = Math.max(1, right - left);
          ctx.font = `${fontVariant}${Math.max(1, targetPreviewPx)}px ${fontFamily}`;
          const measuredWidth = ctx.measureText(displayText).width;
          const shrinkFactor = measuredWidth > boxWidth ? boxWidth / measuredWidth : 1;
          const overflowedTarget = shrinkFactor < 1;
          // Final rendered font: shrunk to fit; only floor-clamped so a very
          // small font_size_min doesn't vanish. No upper cap — the whole point
          // of Max preview is to see the real size.
          const fontSize = Math.max(7, targetPreviewPx * shrinkFactor);

          // Outline colour: red when the target size would overflow (i.e. this
          // preview mode wouldn't fit at the configured size) so the user
          // notices without checking the panel.
          const baseColor = overflowedTarget ? '#c62828' : '#9c27b0';
          const activeColor = overflowedTarget ? '#8e0000' : '#7b1fa2';
          ctx.strokeStyle = (isSelected || isHovered) ? activeColor : baseColor;
          ctx.lineWidth = (isSelected || isHovered) ? 2 : 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(left, ty - halfHeight);
          ctx.lineTo(right, ty - halfHeight);
          ctx.lineTo(right, ty + halfHeight);
          ctx.lineTo(left, ty + halfHeight);
          ctx.closePath();
          ctx.stroke();

          if (isHovered || isSelected) {
            ctx.fillStyle = overflowedTarget ? 'rgba(198, 40, 40, 0.10)' : 'rgba(156, 39, 176, 0.08)';
            ctx.fill();
            ctx.setLineDash([]);
            ctx.fillStyle = baseColor;
            ctx.fillRect(left - 2, ty - 6, 4, 12);
            ctx.fillRect(right - 2, ty - 6, 4, 12);
          }

          ctx.setLineDash([]);
          ctx.font = `${fontVariant}${fontSize}px ${fontFamily}`;
          ctx.fillStyle = tf.font_color || '#000';
          if (tf.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 3; ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1; }
          ctx.textAlign = tf.align || 'center';
          const alignX = tf.align === 'left' ? left + 2 : tf.align === 'right' ? right - 2 : tx;
          // Clip text drawing to the box so it can never bleed past the bounds,
          // even in edge cases (a font that reports smaller measureText than it
          // actually draws — happens with fallback fonts before load).
          ctx.save();
          ctx.beginPath();
          ctx.rect(left, ty - halfHeight, right - left, halfHeight * 2);
          ctx.clip();
          ctx.fillText(displayText, alignX, ty + fontSize * 0.35);
          ctx.restore();
          ctx.textAlign = 'start';
          if (tf.shadow) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; }
        }
      }

      // --- Mascot ---
      if (layer.type === 'mascot' && formData.mascot_center_x != null && formData.mascot_center_y != null && formData.mascot_size != null) {
        const isHoveredMascot = hoveredElement?.type === 'mascot';
        const isSelectedMascot = selectedElement?.type === 'mascot';
        const mx = pctToX(formData.mascot_center_x);
        const my = pctToY(formData.mascot_center_y);
        const ms = pctToX(formData.mascot_size);
        const hs = ms / 2;
        ctx.strokeStyle = (isSelectedMascot || isHoveredMascot) ? '#2e7d32' : '#4caf50';
        ctx.lineWidth = (isSelectedMascot || isHoveredMascot) ? 3 : 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(mx - hs, my - hs, ms, ms);
        ctx.setLineDash([]);
        ctx.fillStyle = '#4caf50';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Mascot', mx, my + 4);
        ctx.textAlign = 'start';
        if (isHoveredMascot || isSelectedMascot) {
          const handleSize = 5;
          ctx.fillStyle = '#4caf50'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5;
          [[mx - hs, my - hs], [mx + hs, my - hs], [mx - hs, my + hs], [mx + hs, my + hs]].forEach(([cx, cy]) => {
            ctx.beginPath(); ctx.rect(cx - handleSize, cy - handleSize, handleSize * 2, handleSize * 2); ctx.fill(); ctx.stroke();
          });
        }
      }
    }); // end layer loop

    // === Fixed elements (not in layer order) ===

    // Crop guide — 5x7 for portrait, 7x5 for landscape
    const isLandscapeCanvas = aspect > 1;
    const cropAspect = isLandscapeCanvas ? 7 / 5 : 5 / 7;
    const cropLabel = isLandscapeCanvas ? '7x5 crop' : '5x7 crop';
    let cropW, cropH;
    if (aspect > cropAspect) { cropH = previewH * 0.92; cropW = cropH * cropAspect; }
    else { cropW = previewW * 0.92; cropH = cropW / cropAspect; }
    const cropX = (previewW - cropW) / 2;
    const cropY = (previewH - cropH) / 2;
    ctx.strokeStyle = 'rgba(0, 150, 255, 0.35)';
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(cropX, cropY, cropW, cropH);
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(0, 150, 255, 0.35)';
    ctx.font = '9px sans-serif';
    ctx.fillText(cropLabel, cropX + 3, cropY + 10);

    // Alignment guides
    if (alignmentGuides.length > 0) {
      ctx.strokeStyle = '#00e676';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      alignmentGuides.forEach(g => {
        ctx.beginPath();
        if (g.axis === 'vertical') { ctx.moveTo(g.pos, 0); ctx.lineTo(g.pos, previewH); }
        else { ctx.moveTo(0, g.pos); ctx.lineTo(previewW, g.pos); }
        ctx.stroke();
      });
    }

    // Canvas dimensions label
    ctx.fillStyle = '#999';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`${cw} x ${ch} @ ${formData.canvas_dpi || 300}dpi`, previewW - 4, 12);
    ctx.textAlign = 'start';

  }, [formData, refImg, canvasBgRefImg, canvasBgImg, overlayImg, collagePhotoImgs, previewW, previewH, aspect, cw, ch, alignmentGuides, hoveredElement, selectedElement, simulationData,
      refZoom, refRotation, refOffsetX, refOffsetY, fontLoadTick, canvasZoom, textSizePreview]);

  const handleZoomChange = (e) => {
    if (!onChange) return;
    onChange({ ...formData, ref_zoom: Number(e.target.value) });
  };
  const handleRotationChange = (e) => {
    if (!onChange) return;
    onChange({ ...formData, ref_rotation: Number(e.target.value) });
  };
  const handleSliderCommit = () => {
    if (onCommit) onCommit();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Zoom toolbar */}
      <div style={{
        padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid #ddd', background: '#f0f0f0', flexShrink: 0
      }}>
        <button
          onClick={zoomOut}
          disabled={canvasZoom <= ZOOM_STEPS[0]}
          style={{
            width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4,
            background: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1,
            color: canvasZoom <= ZOOM_STEPS[0] ? '#ccc' : '#333'
          }}
        >-</button>
        <span style={{ fontSize: 12, color: '#555', minWidth: 42, textAlign: 'center' }}>
          {Math.round(canvasZoom * 100)}%
        </span>
        <button
          onClick={zoomIn}
          disabled={canvasZoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
          style={{
            width: 28, height: 28, border: '1px solid #ccc', borderRadius: 4,
            background: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1,
            color: canvasZoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1] ? '#ccc' : '#333'
          }}
        >+</button>
        {canvasZoom !== 1 && (
          <button
            onClick={() => setCanvasZoom(1)}
            style={{
              padding: '2px 8px', border: '1px solid #ccc', borderRadius: 4,
              background: '#fff', cursor: 'pointer', fontSize: 11, color: '#666'
            }}
          >Reset</button>
        )}
        <span style={{ fontSize: 10, color: '#bbb', marginLeft: 8 }}>Ctrl+scroll to zoom</span>
        <div style={{ flex: 1 }} />
        {refImg && (
          <span style={{ fontSize: 10, color: '#999' }}>
            Shift+drag to move ref photo | Drag face oval independently
          </span>
        )}
      </div>

      {/* Scrollable canvas area */}
      <div
        ref={scrollAreaRef}
        style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'start', justifyContent: 'center', padding: 20 }}
      >
        <div style={{ flexShrink: 0 }}>
          <canvas
            ref={canvasRef}
            tabIndex={0}
            style={{
              width: Math.round(previewW * canvasZoom),
              height: Math.round(previewH * canvasZoom),
              borderRadius: 4,
              boxShadow: '0 2px 12px rgba(0,0,0,.2)',
              background: '#fff',
              cursor: refDrag ? 'move' : 'default',
              imageRendering: canvasZoom >= 3 ? 'pixelated' : 'auto',
              outline: 'none'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          />
        </div>
      </div>

      {/* Ref photo controls — only show when a ref photo is loaded */}
      {refImg && (
        <div style={{
          padding: '8px 12px', borderTop: '1px solid #ddd', background: '#f0f0f0',
          display: 'flex', gap: 16, alignItems: 'center',
          fontSize: 11, color: '#666', flexWrap: 'wrap', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 180 }}>
            <label>Ref Zoom:</label>
            <input
              type="range" min="0.2" max="4" step="0.05"
              value={refZoom}
              onChange={handleZoomChange}
              onMouseUp={handleSliderCommit}
              style={{ flex: 1 }}
            />
            <span style={{ minWidth: 36, textAlign: 'right' }}>{refZoom.toFixed(2)}x</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 180 }}>
            <label>Ref Rotate:</label>
            <input
              type="range" min="-180" max="180" step="1"
              value={refRotation}
              onChange={handleRotationChange}
              onMouseUp={handleSliderCommit}
              style={{ flex: 1 }}
            />
            <span style={{ minWidth: 30, textAlign: 'right' }}>{refRotation}°</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookCanvas;
